export const WIP_CSV_HEADERS = [
  "project_name",
  "opening_balance",
  "opening_year",
  "invoice_number",
  "invoice_date",
  "supplier",
  "description",
  "invoice_amount",
];

export const openingYearToDate = (year, financialYearStart) => {
  if (!year) return null;
  const monthAndDay = String(financialYearStart).slice(5, 10);
  return /^\d{2}-\d{2}$/.test(monthAndDay)
    ? `${String(year)}-${monthAndDay}`
    : null;
};

export const openingDateToYear = (date) =>
  date ? String(date).slice(0, 4) : "";

const validIsoDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  );
};

const parseCsvRows = (text) => {
  const rows = [];
  let values = [];
  let value = "";
  let quoted = false;
  let quoteClosed = false;
  let line = 1;
  let rowLine = 1;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (quoted) {
      if (character === '"' && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (character === '"') {
        quoted = false;
        quoteClosed = true;
      } else {
        value += character;
        if (character === "\n") line += 1;
      }
    } else if (
      quoteClosed &&
      character !== "," &&
      character !== "\n" &&
      character !== "\r"
    ) {
      throw new Error(
        `Unexpected value after a closing quote on row ${rowLine}`,
      );
    } else if (character === '"' && value.length === 0) {
      quoted = true;
    } else if (character === '"') {
      throw new Error(`Unexpected quote on row ${rowLine}`);
    } else if (character === ",") {
      values.push(value);
      value = "";
      quoteClosed = false;
    } else if (character === "\n" || character === "\r") {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      values.push(value);
      rows.push({ line: rowLine, values });
      values = [];
      value = "";
      quoteClosed = false;
      line += 1;
      rowLine = line;
    } else {
      value += character;
    }
  }
  if (quoted)
    throw new Error(`Unclosed quoted value starting on row ${rowLine}`);
  if (value.length > 0 || values.length > 0) {
    values.push(value);
    rows.push({ line: rowLine, values });
  }
  return rows.filter((row) => row.values.some((entry) => entry.trim() !== ""));
};

export const parseWipCsv = (text, existingProjects, financialYearStart) => {
  const errors = [];
  let rows;
  try {
    rows = parseCsvRows(String(text || "").replace(/^\uFEFF/, ""));
  } catch (error) {
    return { errors: [{ row: 1, message: error.message }], projects: [] };
  }
  if (rows.length === 0)
    return {
      errors: [{ row: 1, message: "The CSV file is empty" }],
      projects: [],
    };

  const headers = rows[0].values.map((header) => header.trim().toLowerCase());
  const missingHeaders = WIP_CSV_HEADERS.filter(
    (header) => !headers.includes(header),
  );
  const extraHeaders = headers.filter(
    (header) => !WIP_CSV_HEADERS.includes(header),
  );
  if (
    headers.length !== WIP_CSV_HEADERS.length ||
    new Set(headers).size !== headers.length ||
    missingHeaders.length > 0 ||
    extraHeaders.length > 0
  ) {
    return {
      errors: [
        {
          row: rows[0].line,
          message: `CSV headers must be exactly: ${WIP_CSV_HEADERS.join(", ")}`,
        },
      ],
      projects: [],
    };
  }

  const existingNames = new Set(
    existingProjects.map((project) =>
      String(project.name).trim().toLocaleLowerCase(),
    ),
  );
  const grouped = new Map();
  const today = new Date().toISOString().slice(0, 10);

  rows.slice(1).forEach((csvRow) => {
    if (csvRow.values.length !== headers.length) {
      errors.push({
        row: csvRow.line,
        message: `Expected ${headers.length} columns but found ${csvRow.values.length}`,
      });
      return;
    }
    const row = Object.fromEntries(
      headers.map((header, index) => [header, csvRow.values[index].trim()]),
    );
    const name = row.project_name;
    const projectKey = name.toLocaleLowerCase();
    if (!name || name.length > 100) {
      errors.push({
        row: csvRow.line,
        message: "Project name must contain between 1 and 100 characters",
      });
      return;
    }
    if (existingNames.has(projectKey)) {
      errors.push({
        row: csvRow.line,
        message: `Project '${name}' already exists`,
      });
    }

    let project = grouped.get(projectKey);
    if (!project) {
      if (row.opening_balance === "") {
        errors.push({
          row: csvRow.line,
          message: "The first row for a project must include opening_balance",
        });
      }
      const openingBalance = Number(row.opening_balance);
      if (
        row.opening_balance === "" ||
        !Number.isFinite(openingBalance) ||
        openingBalance < 0
      ) {
        errors.push({
          row: csvRow.line,
          message: "Opening balance must be a non-negative number",
        });
      }
      if (!row.opening_year) {
        errors.push({
          row: csvRow.line,
          message: "Opening year is required",
        });
      } else if (
        !/^\d{4}$/.test(row.opening_year) ||
        !openingYearToDate(row.opening_year, financialYearStart)
      ) {
        errors.push({
          row: csvRow.line,
          message: "Opening year must be a four-digit year",
        });
      }
      const openingDate = openingYearToDate(
        row.opening_year,
        financialYearStart,
      );
      if (openingDate && openingDate > today) {
        errors.push({
          row: csvRow.line,
          message: "Opening year cannot begin in the future",
        });
      }
      project = {
        name,
        opening_balance: openingBalance,
        opening_year: row.opening_year ? Number(row.opening_year) : null,
        invoices: [],
        invoiceNumbers: new Set(),
      };
      grouped.set(projectKey, project);
    } else {
      if (
        row.opening_balance !== "" &&
        Number(row.opening_balance) !== project.opening_balance
      ) {
        errors.push({
          row: csvRow.line,
          message: `Opening balance conflicts with an earlier row for '${name}'`,
        });
      }
      if (
        row.opening_year !== "" &&
        Number(row.opening_year) !== project.opening_year
      ) {
        errors.push({
          row: csvRow.line,
          message: `Opening year conflicts with an earlier row for '${name}'`,
        });
      }
    }

    const invoiceValues = [
      row.invoice_number,
      row.invoice_date,
      row.supplier,
      row.description,
      row.invoice_amount,
    ];
    if (invoiceValues.every((value) => value === "")) return;

    if (!row.invoice_number || row.invoice_number.length > 100)
      errors.push({
        row: csvRow.line,
        message: "Invoice number must contain between 1 and 100 characters",
      });
    if (!validIsoDate(row.invoice_date) || row.invoice_date > today)
      errors.push({
        row: csvRow.line,
        message: "Invoice date must be a non-future date in YYYY-MM-DD format",
      });
    if (!row.supplier || row.supplier.length > 200)
      errors.push({
        row: csvRow.line,
        message: "Supplier must contain between 1 and 200 characters",
      });
    if (row.description.length > 512)
      errors.push({
        row: csvRow.line,
        message: "Description cannot exceed 512 characters",
      });
    const invoiceAmount = Number(row.invoice_amount);
    if (
      row.invoice_amount === "" ||
      !Number.isFinite(invoiceAmount) ||
      invoiceAmount <= 0
    )
      errors.push({
        row: csvRow.line,
        message: "Invoice amount must be a positive number",
      });
    const invoiceKey = row.invoice_number.toLocaleLowerCase();
    if (row.invoice_number && project.invoiceNumbers.has(invoiceKey))
      errors.push({
        row: csvRow.line,
        message: `Invoice '${row.invoice_number}' is duplicated for '${name}'`,
      });
    else if (row.invoice_number) project.invoiceNumbers.add(invoiceKey);

    const openingDate = openingYearToDate(
      project.opening_year,
      financialYearStart,
    );
    if (
      validIsoDate(row.invoice_date) &&
      openingDate &&
      row.invoice_date < openingDate
    )
      errors.push({
        row: csvRow.line,
        message: "Invoice date cannot precede the opening financial year",
      });

    project.invoices.push({
      invoice_number: row.invoice_number,
      invoice_date: row.invoice_date,
      supplier: row.supplier,
      description: row.description,
      amount: invoiceAmount,
    });
  });

  const projects = Array.from(grouped.values()).map(
    ({ invoiceNumbers, ...project }) => project,
  );
  if (projects.length === 0 && errors.length === 0)
    errors.push({ row: 2, message: "The CSV contains no project rows" });
  return { errors, projects };
};
